import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { BarChart3, BookOpenText, FlaskConical, Gamepad2 } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { type PromptInputMessage } from "@/components/ai-elements/prompt-input";
import { ChatInput } from "@/components/chat-input/chat-input";
import { InsufficientCreditsDialog } from "@/components/chat-panel/insufficient-credits-dialog";
import { cn } from "@/components/ui/lib/utils";
import { useSignInAnonymously } from "@/hooks/auth/use-sign-in-anonymously";
import { useBalance } from "@/hooks/query/billing";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { useSession } from "@/lib/auth-client";
import * as m from "@/paraglide/messages";
import { $createThread } from "@/server/actions/thread";

type CopilotCardProps = {
	className?: string;
	heading?: ReactNode;
};

export function CopilotCard({ className, heading }: CopilotCardProps) {
	const navigate = useNavigate();
	const { data: session } = useSession();
	const { mutateAsync: signInAnonymously } = useSignInAnonymously();
	const [input, setInput] = useLocalStorage("chat-input", "");
	const [model, setModel] = useState("lite");
	const [webSearch, setWebSearch] = useState(false);
	const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

	const placeholders = useMemo(
		() => [
			m.landing_copilot_placeholder1(),
			m.landing_copilot_placeholder2(),
			m.landing_copilot_placeholder3(),
			m.landing_copilot_placeholder4(),
		],
		[],
	);

	const suggestions = useMemo(
		() => [
			{
				label: m.landing_copilot_suggLesson(),
				icon: BookOpenText,
				prompt: placeholders[0],
			},
			{
				label: m.landing_copilot_suggQuiz(),
				icon: BarChart3,
				prompt: placeholders[1],
			},
			{
				label: m.landing_copilot_suggLanguage(),
				icon: FlaskConical,
				prompt: placeholders[2],
			},
			{
				label: m.landing_copilot_suggAnimating(),
				icon: Gamepad2,
				prompt: placeholders[3],
			},
		],
		[placeholders],
	);

	const { data: balance } = useBalance();

	const { mutate: createThread, isPending: isSubmitting } = useMutation({
		mutationKey: ["threads", "create"],
		mutationFn: async (message: PromptInputMessage) => {
			if (!session?.user) {
				await signInAnonymously();
			}

			const parts: Array<
				| { type: "text"; text: string }
				| {
						type: "file";
						url: string;
						mediaType: string;
						filename?: string;
				  }
			> = [];

			if (message.text) {
				parts.push({ type: "text", text: message.text });
			}

			if (message.files) {
				for (const file of message.files) {
					if (file.url) {
						parts.push({
							type: "file",
							url: file.url,
							mediaType: file.mediaType,
							filename: file.filename,
						});
					}
				}
			}

			const { threadId } = await $createThread({
				data: {
					message: { role: "user", parts },
					webSearch,
					model,
				},
			});

			if (!threadId) {
				throw new Error("Failed to create thread");
			}
			return { threadId };
		},
		onSuccess: ({ threadId }) => {
			navigate({ to: "/chat/$threadId", params: { threadId } });
		},
		onError: (error) => {
			if (error.message.includes("INSUFFICIENT_CREDITS")) {
				setShowUpgradeDialog(true);
			}
		},
		meta: { error: { enabled: false } },
	});

	const handleSubmit = useCallback(
		(message: PromptInputMessage) => {
			const hasText = !!message.text?.trim();
			const hasFiles = !!message.files && message.files.length > 0;
			if (!(hasText || hasFiles) || isSubmitting) return;
			createThread(message);
			setInput("");
		},
		[isSubmitting, createThread, setInput],
	);

	return (
		<>
			<div
				className={cn(
					"rounded-[28px] bg-secondary px-6 py-10 shadow-[0_20px_50px_-20px_rgba(203,165,64,0.5)] md:px-16 md:py-14",
					className,
				)}
			>
				{heading && (
					<div className="mx-auto mb-8 max-w-xl text-center text-2xl font-semibold leading-snug text-[#272727] md:text-[28px]">
						{heading}
					</div>
				)}
				<div className="mx-auto max-w-3xl rounded-3xl bg-card p-4 shadow-[0_0_25px_rgba(203,165,64,0.45)] md:p-6">
					<ChatInput
						handleSubmit={handleSubmit}
						setInput={setInput}
						input={input}
						placeholder={placeholders[0]}
						animatedPlaceholder={placeholders}
						model={model}
						setModel={setModel}
						webSearch={webSearch}
						setWebSearch={setWebSearch}
						status={isSubmitting ? "submitted" : "ready"}
					/>
				</div>

				<div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
					<span className="text-sm font-semibold text-foreground">
						{m.landing_copilot_try()}
					</span>
					{suggestions.map(({ label, icon: Icon, prompt }) => (
						<button
							key={label}
							type="button"
							onClick={() => setInput(prompt)}
							className="inline-flex items-center gap-1.5 rounded-full border border-foreground bg-card px-3 py-2 text-sm font-semibold text-foreground transition-transform hover:-translate-y-0.5"
						>
							<Icon className="size-3.5" />
							{label}
						</button>
					))}
				</div>
			</div>

			<InsufficientCreditsDialog
				open={showUpgradeDialog}
				onOpenChange={setShowUpgradeDialog}
				balance={balance ?? 0}
			/>
		</>
	);
}
